/**
 * Display preparation for the sleep-disordered breathing screening section.
 *
 * The analysis service classifies each minute of the sequence and rolls the
 * result up per hour (project_management#517/#518). The report shows the
 * hourly roll-up as a printable table — apnea events are discrete, and a
 * per-hour count lets a clinician see night-time clustering without the
 * report having to guess when the patient slept. A table (not a chart) because
 * the report is printed: every number must be legible on paper, and the
 * "level" column is plain text so it survives black-and-white printing.
 *
 * Two things this module deliberately does NOT do:
 *  - map the burden onto AHI severity bands (mild/moderate/severe). The
 *    burden is a surrogate, not an AHI.
 *  - produce any "nothing detected" wording. Sensitivity is limited, so a
 *    negative screen only means "below threshold".
 */
import {
  ApneaHourlyBars,
  ApneaHourlyRow,
  ApneaHourlyTable,
  ApneaMetricsData,
  ApneaTimeSeriesData,
} from '../types/apneaTimeSeries';

export const MINUTES_PER_HOUR = 60;

export const HOUR_IN_SECONDS = 60 * 60;


/**
 * What the interpretation line under the chart should say.
 *
 *  - `flagged`        burden reached the screening threshold
 *  - `belowThreshold` burden stayed under it (NOT "no apnea")
 *  - `notAssessable`  too little assessable night-time data to screen
 *
 * `null` means the payload was internally inconsistent (assessable but no
 * verdict) — nothing is said rather than something wrong.
 */
export type ScreeningOutcome = 'flagged' | 'belowThreshold' | 'notAssessable';

export function isAssessable(summary: ApneaMetricsData | null | undefined): boolean {
  return summary?.assessable === true;
}

export function getScreeningOutcome(
  summary: ApneaMetricsData | null | undefined,
): ScreeningOutcome | null {
  if (!summary) {
    return null;
  }

  if (!isAssessable(summary)) {
    return 'notAssessable';
  }

  if (summary.flagged === true) {
    return 'flagged';
  }

  if (summary.flagged === false) {
    return 'belowThreshold';
  }

  return null;
}

/** Whether the summary carries a burden figure that may be printed. */
export function hasBurdenFigures(summary: ApneaMetricsData | null | undefined): boolean {
  return (
    isAssessable(summary)
    && typeof summary?.burdenMinPerH === 'number'
    && Number.isFinite(summary.burdenMinPerH)
    && typeof summary?.analysedHours === 'number'
    && Number.isFinite(summary.analysedHours)
  );
}

/**
 * Format a min/h figure for text: one decimal, but a whole number stays
 * whole ("15", not "15.0") so a threshold reads as a threshold.
 */
export function formatMinPerHour(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  const rounded = Number(value.toFixed(1));

  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function toFiniteOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Turn the hourly roll-up into drawable bars.
 *
 * Hours with nothing analysed become null in both arrays (a gap). Values are
 * clamped so a bar can never exceed the hour or exceed its own analysed
 * context, and hours are sorted by start time because uPlot requires an
 * ascending x axis.
 */
export function toHourlyBars(data: ApneaTimeSeriesData | null | undefined): ApneaHourlyBars {
  const bars: ApneaHourlyBars = { hourStartSec: [], apneaMinutes: [], analysedMinutes: [] };
  const hourly = data?.hourly;

  if (!hourly?.hourStartSec?.length) {
    return bars;
  }

  const starts = hourly.hourStartSec;
  const apnea = hourly.apneaMinutes ?? [];
  const analysed = hourly.analysedMinutes ?? [];
  const length = Math.min(starts.length, apnea.length, analysed.length);

  const order = Array.from({ length }, (_, i) => i)
    .filter((i) => typeof starts[i] === 'number' && Number.isFinite(starts[i]))
    .sort((a, b) => starts[a] - starts[b]);

  order.forEach((i) => {
    const analysedMinutes = Math.min(MINUTES_PER_HOUR, toFiniteOrZero(analysed[i]));

    bars.hourStartSec.push(starts[i]);

    if (analysedMinutes <= 0) {
      bars.apneaMinutes.push(null);
      bars.analysedMinutes.push(null);
    } else {
      const apneaMinutes = Math.max(0, Math.min(analysedMinutes, toFiniteOrZero(apnea[i])));

      bars.apneaMinutes.push(apneaMinutes);
      bars.analysedMinutes.push(analysedMinutes);
    }
  });

  return bars;
}

/** Whether at least one hour was analysed and is therefore worth drawing. */
export function hasDisplayableData(bars: ApneaHourlyBars): boolean {
  return bars.analysedMinutes.some((v) => v !== null);
}

/** An hour with fewer analysed minutes than this is shown as "not assessable". */
export const MIN_ASSESSABLE_MINUTES_PER_HOUR = 10;

/** One level block per this many apnea-pattern minutes (60 min = 12 blocks). */
export const LEVEL_MINUTES_PER_BLOCK = 5;

export const LEVEL_MAX_BLOCKS = MINUTES_PER_HOUR / LEVEL_MINUTES_PER_BLOCK;

/** Local clock time "HH:MM" for a sequence offset. */
export function formatClockTime(startTimeMs: number, offsetSec: number): string {
  const d = new Date(startTimeMs + offsetSec * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  return `${hh}:${mm}`;
}

/**
 * Hourly rows for the printed table.
 *
 * Per row: local clock range, analysed minutes, apnea-pattern minutes, the
 * share of analysed minutes, a text level (one block per 5 min) and whether
 * the hour on its own reaches the screening threshold (min/h, scaled to the
 * analysed minutes so a half-recorded hour is not misread). Hours with fewer
 * than MIN_ASSESSABLE_MINUTES_PER_HOUR analysed minutes are marked not
 * assessable and carry no apnea figure — "nothing measured" must never read
 * as "nothing found".
 */
export function toHourlyRows(
  data: ApneaTimeSeriesData | null | undefined,
  startTimeMs: number,
  thresholdMinPerH: number | null | undefined,
): ApneaHourlyTable {
  const bars = toHourlyBars(data);
  const rows: ApneaHourlyRow[] = [];
  let totalAnalysed = 0;
  let totalApnea = 0;
  let elevatedHours = 0;

  bars.hourStartSec.forEach((startSec, i) => {
    const analysed = bars.analysedMinutes[i] ?? 0;
    const apnea = bars.apneaMinutes[i] ?? 0;
    const assessable = analysed >= MIN_ASSESSABLE_MINUTES_PER_HOUR;
    const share = assessable ? apnea / analysed : null;
    const ratePerHour = assessable ? (apnea / analysed) * MINUTES_PER_HOUR : null;
    const elevated = assessable
      && typeof thresholdMinPerH === 'number'
      && Number.isFinite(thresholdMinPerH)
      && (ratePerHour as number) >= thresholdMinPerH;
    const levelBlocks = assessable
      ? Math.min(LEVEL_MAX_BLOCKS, Math.ceil(apnea / LEVEL_MINUTES_PER_BLOCK))
      : 0;

    if (assessable) {
      totalAnalysed += analysed;
      totalApnea += apnea;
    }

    if (elevated) {
      elevatedHours += 1;
    }

    rows.push({
      startSec,
      timeRange: `${formatClockTime(startTimeMs, startSec)}–${formatClockTime(startTimeMs, startSec + HOUR_IN_SECONDS)}`,
      analysedMinutes: analysed,
      apneaMinutes: assessable ? apnea : null,
      share,
      levelBlocks,
      assessable,
      elevated,
    });
  });

  return {
    rows,
    totalAnalysedMinutes: totalAnalysed,
    totalApneaMinutes: totalApnea,
    totalShare: totalAnalysed > 0 ? totalApnea / totalAnalysed : null,
    elevatedHours,
  };
}

/** "23 %" style share, or a dash when the hour was not assessable. */
export function formatShare(share: number | null): string {
  return share === null ? '–' : `${Math.round(share * 100)} %`;
}

/** Text bar for the level column: printable, monochrome, no hover needed. */
export function formatLevel(blocks: number): string {
  return '■'.repeat(Math.max(0, blocks));
}
